// SH-2102: expands SH-1418's "Activities" chip into a per-Activity-Type
// checkbox filter. Fetches GET /activityTypes independently (plain local
// fetch, same style as useCalendarEvents.ts) rather than reusing the
// Activity feature's React Context, which is scoped to Lead/Opportunity
// Activity editing and isn't mounted around the Calendar route.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '@user/store/hooks'
import { get } from '@user/store/services/crud'
import { ServiceConfig } from '@user/store/services/config'
import { logger } from '@user/utils/logger'
import { useDebounce } from '@user/utils/useDebounce'
import {
  IUserPreferenceGeneralListItemType,
  selectUserPreferencesGeneralLoaded,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import type { IUserPreferenceGeneralListItem } from '@user/store/userPreferences/userPreferencesGeneral'
import type { CalendarEvent } from '../typings'
import { useQueuedPreferenceWrite } from './generalPreferencesLock'

export type ActivityType = {
  id: string
  name: string | null
  description: string | null
}

/** GET /activityTypes' wire shape — id is numeric (ActivityTypeDTO.Id is a `long`). */
type ActivityTypeWire = {
  id: number
  name: string | null
  description: string | null
}

const NONE_SENTINEL = 'none'
const TOKEN_SEPARATOR = ','

function findPrefValue(
  prefs: IUserPreferenceGeneralListItem[],
  type: IUserPreferenceGeneralListItemType,
): string | undefined {
  return prefs.find((row) => row?.type === type)?.value
}

function decodeTokenSet(rawValue: string): Set<string> {
  if (rawValue === NONE_SENTINEL) return new Set()
  return new Set(rawValue.split(TOKEN_SEPARATOR).filter(Boolean))
}

export function encodeIds(ids: Iterable<string>): string {
  const list = [...ids]
  return list.length > 0 ? list.join(TOKEN_SEPARATOR) : NONE_SENTINEL
}

/**
 * saved ∩ current ∪ (current not in known) — mirrors resolveKnockStatuses
 * (features/MapView/FilterDrawer/hooks/useData.ts): `savedIds === undefined`
 * means the row is absent (never configured) => every current id selects.
 * Otherwise a type present in `current` but missing from `knownIds` is
 * genuinely new and defaults selected; one present in `knownIds` but absent
 * from `savedIds` was deliberately unchecked and stays off.
 */
export function reconcileSelectedIds(
  currentIds: string[],
  savedIds: Set<string> | undefined,
  knownIds: Set<string>,
): Set<string> {
  if (savedIds === undefined) return new Set(currentIds)
  const valid = currentIds.filter((id) => savedIds.has(id))
  const fresh = currentIds.filter((id) => !knownIds.has(id))
  return new Set([...valid, ...fresh])
}

export function computeChipLabel(
  selectedCount: number,
  totalCount: number,
): string {
  if (selectedCount === 0 || selectedCount === totalCount) return 'Activities'
  if (selectedCount === 1) return 'Activities (1)'
  return `Activities (${selectedCount})`
}

function idsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}

export type UseActivityTypeFilterResult = {
  activityTypes: ActivityType[]
  selectedIds: Set<string>
  toggleType: (id: string) => void
  selectAll: () => void
  clearAll: () => void
  chipLabel: string
  isAllOff: boolean
  isVisible: (event: CalendarEvent) => boolean
}

export function useActivityTypeFilter(): UseActivityTypeFilterResult {
  const prefs = useAppSelector(userGeneralPreferencesList)
  const prefsLoaded = useAppSelector(selectUserPreferencesGeneralLoaded)
  // See useCalendarFilterPreferences.ts for why this must be read via a ref:
  // userGeneralPreferencesList (entityAdapter's selectAll) returns a new
  // array reference on every Redux dispatch app-wide.
  const prefsRef = useRef(prefs)
  prefsRef.current = prefs

  // activityTypes/loading always change together (both branches of the fetch
  // below set both) -- one state object, one render, not two.
  const [typesState, setTypesState] = useState<{
    activityTypes: ActivityType[]
    loading: boolean
  }>({ activityTypes: [], loading: true })
  const { activityTypes, loading: typesLoading } = typesState
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Always the latest, independent of the 500ms debounce below -- read on
  // unmount to flush a toggle made just before navigating away (see the
  // matching comment in useCalendarFilterPreferences.ts).
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds

  const hydrated = useRef(false)
  // The empty selectedIds set is also the state before anything has loaded, so
  // `isVisible` can't tell "still loading" from "user unselected everything" on
  // its own. Set once the selection reflects a real source: hydration finished
  // or the user chose. `hydrated` is a ref and doesn't re-render, hence state.
  const [selectionReady, setSelectionReady] = useState(false)
  const lastKnownRef = useRef<Set<string>>(new Set())
  const knownIdsRef = useRef<Set<string>>(new Set())
  const pendingKnownWrite = useRef(false)
  // activityTypes and prefsLoaded resolve independently, so a click can land
  // in the window after types arrive but before prefs do. Tracks that so
  // hydration merges fresh (never-seen) ids on top of the user's own choice
  // instead of unconditionally overwriting it with the server-reconciled
  // value once prefs catch up a moment later.
  const userInteractedRef = useRef(false)
  // Snapshot of every id the user could already see at the moment they first
  // interacted. Hydration merges in ids missing from THIS set, not from the
  // persisted knownIds -- on a genuine first visit knownIds is empty, so
  // comparing against it would treat every id as "fresh" and silently
  // re-select everything the user had just cleared.
  const interactionBaselineRef = useRef<Set<string> | null>(null)

  // Fetch the configured Activity Types once.
  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    get(
      `${ServiceConfig.baseAPIUrl}${ServiceConfig.activityTypes.activityTypes}`,
      {},
      { signal: controller.signal },
    )
      .then((response) =>
        response.ok
          ? (response.json() as Promise<ActivityTypeWire[]>)
          : Promise.reject(
              new Error(`Failed to load activity types (${response.status})`),
            ),
      )
      .then((data) => {
        if (cancelled) return
        // Normalize to string here so every id comparison downstream (Sets,
        // persisted tokens, event.activityTypeId) compares like for like.
        const activityTypes = data.map((t) => ({ ...t, id: String(t.id) }))
        setTypesState({ activityTypes, loading: false })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Fail-open (E2 error case): chip renders with no rows, no filtering.
        logger.error('Failed to load activity types:', err)
        setTypesState({ activityTypes: [], loading: false })
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [])

  // Hydrate the selection once both the preferences and the type list are
  // ready -- reconciliation needs the *current* type list to run.
  useEffect(() => {
    if (!prefsLoaded || typesLoading || hydrated.current) return
    hydrated.current = true

    const currentIds = activityTypes.map((t) => t.id)
    const savedRaw = findPrefValue(
      prefsRef.current,
      IUserPreferenceGeneralListItemType.UnifiedCalendarFilteredActivityTypes,
    )
    const knownRaw = findPrefValue(
      prefsRef.current,
      IUserPreferenceGeneralListItemType.UnifiedCalendarKnownActivityTypeIds,
    )
    const savedIds =
      savedRaw === undefined ? undefined : decodeTokenSet(savedRaw)
    const knownIds =
      knownRaw === undefined ? new Set<string>() : decodeTokenSet(knownRaw)

    const reconciled = reconcileSelectedIds(currentIds, savedIds, knownIds)
    const nextKnownIds = new Set([...knownIds, ...currentIds])

    lastKnownRef.current = reconciled
    knownIdsRef.current = nextKnownIds
    // A genuinely new type appeared -- the known-ids baseline must grow even
    // though the selection itself may equal what was already saved.
    pendingKnownWrite.current = nextKnownIds.size !== knownIds.size

    if (userInteractedRef.current) {
      // The user already toggled/selected/cleared before prefs caught up --
      // their choice wins. Still merge in anything genuinely new (appeared
      // after they could see it) so it isn't silently left off.
      const baseline = interactionBaselineRef.current ?? new Set(currentIds)
      const fresh = currentIds.filter((id) => !baseline.has(id))
      setSelectedIds((prev) => new Set([...prev, ...fresh]))
    } else {
      setSelectedIds(reconciled)
    }
    // Batched with the selection update above, so there is never a render with
    // the filter ready but the selection still empty.
    setSelectionReady(true)
  }, [prefsLoaded, typesLoading, activityTypes])

  const markInteracted = useCallback(() => {
    if (userInteractedRef.current) return
    userInteractedRef.current = true
    interactionBaselineRef.current = new Set(activityTypes.map((t) => t.id))
    setSelectionReady(true)
  }, [activityTypes])

  const toggleType = useCallback(
    (id: string) => {
      markInteracted()
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    },
    [markInteracted],
  )

  const selectAll = useCallback(() => {
    markInteracted()
    setSelectedIds(new Set(activityTypes.map((t) => t.id)))
  }, [activityTypes, markInteracted])

  const clearAll = useCallback(() => {
    markInteracted()
    setSelectedIds(new Set())
  }, [markInteracted])

  const isVisible = useCallback(
    (event: CalendarEvent) => {
      if (event.source !== 'sales_center') return true
      if (event.subType && event.subType !== 'activity_reminder') return true
      // Fail open until the selection is real (still loading, so the empty Set
      // means "unknown", not "none") and when no types could be loaded (E2:
      // nothing to filter by). Otherwise every page load would flash an empty
      // calendar, and a failed GET /activityTypes would hide every Activity.
      if (!selectionReady || activityTypes.length === 0) return true
      // Every type unselected (Clear all, or unchecking the last one): no
      // Activity shows (SH-2483), including ones with no type to match.
      if (selectedIds.size === 0) return false
      // With a partial selection, fail open rather than hiding an event we
      // can't classify.
      if (!event.activityTypeId) return true
      return selectedIds.has(event.activityTypeId)
    },
    [selectedIds, selectionReady, activityTypes.length],
  )

  // Filters apply instantly above; persistence is debounced (500ms) so a
  // burst of checkbox clicks collapses into one write, mirroring
  // useCalendarFilterPreferences.ts's mitigation of the GeneralPreference
  // read-modify-write race.
  const debouncedIds = useDebounce(selectedIds, 500)

  const handlePersistError = useCallback((err: unknown) => {
    logger.error('Failed to persist activity type filter:', err)
  }, [])
  const persistRows = useQueuedPreferenceWrite(handlePersistError)

  const persist = useCallback(
    (next: Set<string>) => {
      persistRows([
        {
          type: IUserPreferenceGeneralListItemType.UnifiedCalendarFilteredActivityTypes,
          value: encodeIds(next),
        },
        {
          type: IUserPreferenceGeneralListItemType.UnifiedCalendarKnownActivityTypeIds,
          value: encodeIds(knownIdsRef.current),
        },
      ])
    },
    [persistRows],
  )

  useEffect(() => {
    if (!hydrated.current) return
    const changed = !idsEqual(debouncedIds, lastKnownRef.current)
    if (!changed && !pendingKnownWrite.current) return
    lastKnownRef.current = debouncedIds
    pendingKnownWrite.current = false
    persist(debouncedIds)
  }, [debouncedIds, persist])

  // Flush a still-pending toggle -- or a known-ids baseline seeded during
  // hydration that hasn't been saved yet -- on unmount (route change before
  // the debounce fires). Same fix as useCalendarFilterPreferences.ts, plus
  // the pendingKnownWrite check: hydration can set it while leaving
  // selectedIds itself unchanged (a new type was auto-selected into both
  // refs at once), so idsEqual alone would miss it.
  useEffect(() => {
    return () => {
      if (!hydrated.current) return
      const changed = !idsEqual(selectedIdsRef.current, lastKnownRef.current)
      if (!changed && !pendingKnownWrite.current) return
      lastKnownRef.current = selectedIdsRef.current
      pendingKnownWrite.current = false
      persist(selectedIdsRef.current)
    }
  }, [persist])

  const chipLabel = computeChipLabel(selectedIds.size, activityTypes.length)
  const isAllOff = activityTypes.length > 0 && selectedIds.size === 0

  return {
    activityTypes,
    selectedIds,
    toggleType,
    selectAll,
    clearAll,
    chipLabel,
    isAllOff,
    isVisible,
  }
}
